import _ from 'lodash'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Stack } from '@mui/material'

import { useGetNetworkQuery, useReplyNetworkJoinRequestMutation } from '@/core/services/network'

import NotFoundPage from '@/pages/error/NotFoundPage'

import { getApiErrorMessage } from '@/utils/errors'

import MemberRequests from '../components/MemberRequests'
import { useNetworkMemberPermissions } from '../hooks/useNetworkMemberPermissions'

export default function MemberRequestsContainer() {
  const { networkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: network } = useGetNetworkQuery(networkId ?? '', { skip: !networkId })
  const [replyNetworkJoinRequest, { isLoading: isReplying, originalArgs: replyNetworkJoinRequestArgs }] = useReplyNetworkJoinRequestMutation()
  const { isNetworkAdmin } = useNetworkMemberPermissions({ networkId })

  const { action, applicant } = replyNetworkJoinRequestArgs ?? {}

  const pendingMembers = useMemo(
    () =>
      _.map(network?.pendingMembers, (pendingMember) => ({
        id: pendingMember._id,
        isAccepting: isReplying && action === 'accept' && applicant === pendingMember._id,
        isDeclining: isReplying && action === 'decline' && applicant === pendingMember._id,
        fullName: `${pendingMember.userInfo.name} ${pendingMember.userInfo.surname}`,
      })),
    [action, applicant, isReplying, network]
  )

  if (!network) return null

  if (!isNetworkAdmin) {
    return (
      <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
        <NotFoundPage />
      </Stack>
    )
  }

  return (
    <MemberRequests
      onAccept={async (memberId) => {
        if (!networkId) return
        try {
          await replyNetworkJoinRequest({ networkId, action: 'accept', applicant: memberId }).unwrap()
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.network.acceptJoinRequest.error')), { variant: 'error' })
        }
      }}
      pendingMembers={pendingMembers}
    />
  )
}
